import axios from "axios";
import { Flex, LoadingIcon } from "basis";
import { Account } from "library/accounts/types";
import { getHref } from "library/api";
import { useAccountInfo } from "library/hooks/useAccountInfo";
import { useLayout } from "library/hooks/useLayout";
import { checkFields } from "library/utils";
import { Dispatch, FC, useEffect, useReducer } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "../Layout/Layout";
import { Error as ErrorScreen } from "./Error/Error";
import { JointAccount } from "./JointAccount/JointAccount";
import { NoEmail } from "./NoEmail/NoEmail";
import { Otp } from "./Otp/Otp";
import { initialState as init } from "./reducer/initialState";
import { statementPreferenceReducer } from "./reducer/reducer";
import {
  Action,
  ActionType,
  PageInfo,
  PageType,
  ScreenComponentType,
  ScreenState,
  State,
} from "./reducer/types";
import {
  PersonalDetailsMasked,
  PersonalDetailsResponse,
  StatementPreferencesResponse,
} from "./response/response";
import { StatementPreferenceForm } from "./StatementPreferenceForm/StatementPreferenceForm";
import { Success } from "./Success/Success";
import { extractPersonalDetails } from "./utils";

const HEADING = "Statement preferences";

const pageInfo: PageInfo = {
  [PageType.LANDING]: {
    screenComponent: () => null,
  },
  [PageType.NO_EMAIL]: {
    screenComponent: NoEmail,
  },
  [PageType.SUCCESS]: {
    screenComponent: Success,
  },
  [PageType.ERROR]: {
    screenComponent: ErrorScreen,
  },
  [PageType.STATEMENT_PREFERENCE]: {
    screenComponent: StatementPreferenceForm,
  },
  [PageType.JOINT_ACCOUNT]: {
    screenComponent: JointAccount,
  },
  [PageType.OTP]: {
    screenComponent: Otp,
  },
};

type ScreenProps = {
  screenComponent: ScreenComponentType;
  state: ScreenState;
  dispatch: Dispatch<Action>;
};

const StatementPreferenceScreen: FC<ScreenProps> = ({
  screenComponent: ScreenComponent,
  state,
  dispatch,
}) => {
  const { scrollToTop } = useLayout();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, ScreenComponent]);

  return <ScreenComponent {...{ state, dispatch }} />;
};

interface Props {
  location: {
    state: { account: Account };
  };
  initialState: State;
}

export const StatementPreference = ({
  location: {
    state: { account },
  },
  initialState = init,
}: Props) => {
  const navigate = useNavigate();

  const accountInfo = useAccountInfo(account);
  const [state, dispatch] = useReducer(
    statementPreferenceReducer,
    initialState,
  );

  const personalDetails = account.links.find(
    (link) => link.rel === "get_personal_details",
  );
  const statementPreferences = account.links.find(
    (link) => link.rel === "statement-preferences",
  );

  const personalDetailsUnmasked = account.links.find(
    (link) => link.rel === "get_personal_details_unmasked",
  );

  const { page } = state;
  const isLandingPage = page === PageType.LANDING;
  const { screenComponent } = pageInfo[page];

  useQuery(
    ["statement-preferences", account.account_id],
    async () => {
      let createSessionUrl;
      try {
        if (personalDetailsUnmasked && statementPreferences) {
          const res = await Promise.all([
            getHref<PersonalDetailsResponse>(personalDetailsUnmasked?.href),
            getHref<StatementPreferencesResponse>(statementPreferences?.href),
          ]);
          return res;
        }
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          error.response?.data.type === "2FA_REQUIRED"
        ) {
          createSessionUrl = error.response.data.links.find(
            (link) => link.rel === "sessions",
          )?.href;
        } else {
          throw new Error("Personal details or statement preferences missing");
        }
      }
      if (personalDetails && statementPreferences) {
        return Promise.all([
          getHref<PersonalDetailsMasked>(personalDetails.href),
          getHref<StatementPreferencesResponse>(statementPreferences.href),
          Promise.resolve(createSessionUrl),
        ]);
      }
      throw new Error("Personal details or statement preferences missing");
    },
    {
      onSuccess([
        { data: personalDetailsData },
        { data: statementPreferencesData },
        createSessionUrl,
      ]) {
        if (!accountInfo) return;

        if (!personalDetailsData.email) {
          dispatch({
            type: ActionType.NO_EMAIL,
            accountInfo,
            personalDetails: extractPersonalDetails(personalDetailsData),
          });
        }

        dispatch({
          type: ActionType.ALL_DATA_LOADED,
          accountInfo,
          submitUrl: statementPreferences?.href as string,
          personalDetailsUnmaskedUrl: personalDetailsUnmasked?.href as string,
          createSessionUrl,
          personalDetails: extractPersonalDetails(personalDetailsData),
          statementPreferences: {
            preference: statementPreferencesData.pref,
            fee: statementPreferencesData.fee,
            feeWaived: statementPreferencesData.fee_waive,
            feeWaivedExpiry: statementPreferencesData.fee_waive_expiry,
          },
        });
      },
      onError(e) {
        if (!accountInfo) return;
        dispatch({ type: ActionType.ERROR, accountInfo });
      },
      enabled: isLandingPage && !!accountInfo && !account.joint_account,
    },
  );

  useEffect(() => {
    if (!account) {
      navigate("/");
    } else if (accountInfo && account.joint_account) {
      dispatch({ type: ActionType.JOINT_ACCOUNT, accountInfo });
    }
  }, [account, navigate, accountInfo]);

  return (
    <Layout>
      <Layout.Header>{HEADING}</Layout.Header>
      <Layout.Main>
        {checkFields(state, "accountInfo") ? (
          <StatementPreferenceScreen
            screenComponent={screenComponent}
            state={state}
            dispatch={dispatch}
          />
        ) : (
          <Flex height="200" placeItems="center">
            <LoadingIcon size="medium" />
          </Flex>
        )}
      </Layout.Main>
    </Layout>
  );
};
